/**
 * XSS 방어 유틸리티
 *
 * DOMPurify를 사용하여 사용자 입력 및 외부 데이터를 무해화(sanitize)한다.
 * 모든 외부 입력은 DOM에 삽입하거나 API로 전송하기 전에 반드시 이 함수를 거쳐야 한다.
 */

import DOMPurify from 'dompurify';

// ─────────────────────────────────────────────────────────────
// 위험 프로토콜 목록
// ─────────────────────────────────────────────────────────────

/** XSS 공격에 악용될 수 있는 URL 프로토콜 */
const DANGEROUS_PROTOCOLS = ['javascript:', 'data:', 'vbscript:'] as const;

// ─────────────────────────────────────────────────────────────
// 무해화 함수
// ─────────────────────────────────────────────────────────────

/**
 * 순수 텍스트 무해화 — HTML 태그를 모두 제거
 *
 * 사용처: 사용자명, 이메일, 트랙 제목, 아티스트명 등
 */
export const sanitizeText = (text: string | null | undefined): string => {
  if (!text) return '';
  return DOMPurify.sanitize(text, { ALLOWED_TAGS: [] });
};

/**
 * HTML 콘텐츠 무해화 — 안전한 태그만 허용
 *
 * 사용처: 설명글, 바이오, 서식이 있는 텍스트
 */
export const sanitizeHTML = (html: string | null | undefined): string => {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'a', 'ul', 'ol', 'li'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
  });
};

/**
 * 폼 입력값 무해화 — 앞뒤 공백 제거 후 HTML 태그를 모두 제거
 *
 * 사용처: 폼 입력 필드, 편집 가능한 영역
 */
export const sanitizeInput = (input: string | null | undefined): string => {
  if (!input) return '';
  return DOMPurify.sanitize(input.trim(), { ALLOWED_TAGS: [] });
};

/**
 * URL 무해화 — javascript:, data:, vbscript: 등 위험 프로토콜 차단
 *
 * 사용처: 이미지 src, 링크 href
 */
export const sanitizeURL = (url: string | null | undefined): string => {
  if (!url) return '';

  const trimmed = url.trim();
  const lower = trimmed.toLowerCase();

  if (DANGEROUS_PROTOCOLS.some(protocol => lower.startsWith(protocol))) {
    return '';
  }

  return trimmed;
};
